import React, {useEffect} from 'react';
import {checkNull, checkNullStr, getDefStr, isBoolTrue, setTabTitle} from "../../common/fusion-ui/utils/ReactUtils";
import {Anchor, Flexbox, GridBox, MsgBox} from "../../common/fusion-ui";
import {useTheme} from "../../common/ProjectThemeProvider";
import {AdminModuleRouteUrls, ShowViewType} from "../config/AdminConfig";
import useScreenSize from "../../common/fusion-ui/hook/useScreenSize";
import {AdminInterface} from "../components";
import {InvalidPanel, ThemedButton, ThemedDialog, ThemedDrawer, ThemedIconButton} from "../../common/components";
import {AnimationType, FramerAnimator} from "../../common/fusion-ui/animation/FramerAnimator";
import {IsProd} from "../../common/CommonConfig";
import {IconClass} from "../../common/fusion-ui/config/FusionConfig";

/**
 * Create form-page root component with basic features like root-layout, title, back etc.
 *
 * @example
 * <FormPageRoot
 *    tag={TAG}
 *    moduleName={moduleName}
 *    addPageTitle={getTabTitle()}
 *    editPageTitle={getTabTitle()}
 *    updateOnly={isUpdateMode()}
 *    isDataLoading={getField(Fields.isDataLoading)}
 *    isDataFound={getField(Fields.isDataFound)}
 *    dataLoadingMsg={getField(Fields.dataLoadingMsg)}
 *    open={open}
 *    onHide={() => {
 *        if (onHide) onHide();
 *        clearPanelData();
 *    }}
 *    showAs={showAs}>
 *       form-content
 * </FormPageRoot>
 */
const FormPageRoot = React.memo(({
                                     tag,
                                     moduleName,
                                     updateOnly,
                                     isDataLoading,
                                     isDataFound,
                                     dataLoadingMsg,
                                     editPageTitle,
                                     addPageTitle,
                                     showBack = true,
                                     backUrl,
                                     md = 6,
                                     sm = 12,
                                     drawerWidth = '50%',

                                     open,
                                     showAs = ShowViewType.PAGE,

                                     animateChildren = true,
                                     children,
                                     freeComponent,

                                     panelMsg,
                                     panelMsgColor,
                                     isSubmitLoading,
                                     isSubmitDisabled,
                                     onHide,
                                     onSubmit,
                                     onPageRefresh,
                                 }) => {
    const TAG = "FormPageRoot";

    const theme = useTheme();
    const {isScreenUpSm, isScreenBeyondSm} = useScreenSize();

    const animator = new FramerAnimator();

    useEffect(() => {
        if (showAs == ShowViewType.PAGE) {
            setTabTitle(getTabTitle());
        }
    }, [showAs, updateOnly, editPageTitle, addPageTitle]);
    // useEffect(() => {
    //     console.log(TAG, 'useEffect.Re-render-test');
    // }, [])
    return (<>
        {renderViewAsPanel()}
    </>);

    function renderViewAsPanel() {
        switch (showAs) {
            case ShowViewType.PAGE:
                return renderPage();
            case ShowViewType.PANEL:
                return renderPanel();
            case ShowViewType.DIALOG:
                return renderDialog();
            case ShowViewType.DRAWER:
                return renderDrawer();
        }
    }

    function renderDialog() {
        return <ThemedDialog
            open={open}
            title={getTabTitle()}
            widthClass={`col-sm-10 col-md-${md}`}
            className={"rounded-5 p-0 m-0 "}
            titleClassName={'fs-lg mb-2x'}
            contentClassName={'pb-3 mb-3x mt-2 px-4'}
            maxHeight={'80vh'}
            showClose={true}
            showFooter={false}
            closeOnEscape={true}
            closeOnOutsideClick={false}
            onHide={onHide}
            onClose={onHide}
            onCancel={onHide}
            onEscapeClose={onHide}
        >
            {renderPanel()}
        </ThemedDialog>
    }

    function renderDrawer() {
        return <ThemedDrawer
            title={getTabTitle()}
            open={open}
            closeOnEscape={true}
            closeOnClickAway={false}
            padded={false}
            onClose={onHide}
            width={drawerWidth}
            titleAt={'start'}
            titleClassName={'px-2 fs-lg'}
            contentClassName={'px-2'}
            headerContent={<div className={'me-4'}>
                {renderHeaderControls()}
            </div>}>
            {renderPanel()}
        </ThemedDrawer>
    }

    function renderPage() {
        return <AdminInterface
            moduleName={moduleName}
            tag={tag}
            heading={getTabTitle()}
            contentProps={{
                headingAt: 'center'
            }}>
            {/*<GridBox className={'px-1'} sm={10} md={md} justifyAt={'center'}>*/}
            {/*    <Section*/}
            {/*        className={`px-4 py-2 h-100 pb-3 app-section`}*/}
            {/*        title={getTabTitle()}*/}
            {/*        contentClassName={'px-2 py-1 position-relative'}*/}
            {/*        titleClassName={'fs-lg mb-2'}*/}
            {/*        titleColor={theme.primary}*/}
            {/*        overflow={''}*/}
            {/*        borderless={true}*/}
            {/*        shadowVariant={theme.boxShadowMdSm}>*/}
            {renderPanel()}
            {/*</Section>*/}
            {/*</GridBox>*/}
        </AdminInterface>
    }

    function renderPanel() {
        return <div className={'m-0 p-0 px-2x w-100 position-relative d-flex jc-center al-start'}>
            <GridBox className={'px-1x w-100'}
                     md={showAs == ShowViewType.PAGE ? md : 12}
                     sm={showAs == ShowViewType.PAGE ? sm : 12}
                     justifyAt={'center'}>
                <div>
                    <div>
                        {freeComponent && freeComponent}
                    </div>

                    <InvalidPanel
                        wrapperClassName={''}
                        labelClassName={'m-2 w-100 fs-normal'}
                        color={theme.text70}
                        success={isBoolTrue(isDataFound)}
                        loading={isBoolTrue(isDataLoading)}
                        msg={dataLoadingMsg}
                        show404Image={true}
                    />

                    {/*content*/}
                    {((!isBoolTrue(isDataLoading)) && isBoolTrue(isDataFound)) && <div className={'position-relativex'}>
                        {/*header*/}
                        {((isBoolTrue(showBack) && showAs == ShowViewType.PAGE && isScreenBeyondSm && checkNullStr(getDefStr(backUrl, AdminModuleRouteUrls[moduleName])))) &&
                            <div className={'position-absolute'} style={{top: '-2.2rem', right: '.1rem'}}>
                                {renderHeaderControls()}
                            </div>}

                        {isBoolTrue(animateChildren) ? animator.setAnimationType(AnimationType.SLIDE_RIGHT).renderChildren(children) : children}

                        {/*footer*/}
                        <div className={'mt-3'}>
                            {/*msg-box*/}
                            {checkNullStr(panelMsg) && <MsgBox color={getDefStr(panelMsgColor, theme.text)}>
                                {panelMsg}
                            </MsgBox>}

                            {/*controls*/}
                            {animator.setAnimationType(AnimationType.SLIDE_UP).setDuration(300).setDelay(250).render(<Flexbox className={'mt-2'} justifyAt={'center'}
                                                                                                                              alignAt={'center'}>
                                {/*cancel*/}
                                {(showAs == ShowViewType.DRAWER || showAs == ShowViewType.DIALOG) && <ThemedButton
                                    className={'rounded-2 w-auto px-4 py-1'}
                                    text={'Cancel'}
                                    variant={theme.text10}
                                    hoverBgColor={theme.text30}
                                    color={theme.text}
                                    onClick={() => {
                                        if (onHide) onHide();
                                    }}
                                    mx={2}
                                />}

                                {/*submit*/}
                                <ThemedButton
                                    className={'rounded-2 w-auto px-4 py-1'}
                                    text={isBoolTrue(isSubmitLoading) ? isBoolTrue(updateOnly) ? 'Updating...' : 'Saving...' : isBoolTrue(updateOnly) ? 'Update' : 'Save'}
                                    variant={theme.primaryBlue}
                                    hoverBgColor={theme.primaryBlueDark}
                                    loadingWidth={'auto'}
                                    asLoading={isBoolTrue(isSubmitLoading)}
                                    disabled={isBoolTrue(isSubmitDisabled)}
                                    onClick={onSubmit}
                                />
                            </Flexbox>)}
                        </div>
                    </div>}
                </div>
            </GridBox>
        </div>
    }

    function renderHeaderControls() {
        return <Flexbox alignAt={'center'}>
            {/*refresh-btn (for local only)*/}
            {(!IsProd && isBoolTrue(updateOnly) && checkNull(onPageRefresh)) && <ThemedIconButton
                className={'fs-normal'}
                tooltip={`Refresh Form`}
                icon={IconClass.refresh}
                outlined={true}
                borderWidth={0}
                mx={4}
                disabled={isBoolTrue(isDataLoading)}
                onClick={onPageRefresh}
            />}

            {/*back-btn*/}
            {(showAs == ShowViewType.PAGE) && <Anchor
                className={''}
                textAt={'start'}
                iconClass={'bi bi-arrow-left text-primary'}
                href={getDefStr(backUrl, AdminModuleRouteUrls[moduleName])}>
                Back
            </Anchor>}
        </Flexbox>
    }

    function getTabTitle() {
        let name = moduleName + '';
        if (name.endsWith('s')) {
            name = name.slice(0, -1);
        }
        return isBoolTrue(updateOnly) ? getDefStr(editPageTitle, `Edit ${name}`) : getDefStr(addPageTitle, `Add ${name}`);
    }

});
export default FormPageRoot;
